import {isValidEmail} from '@wandb/cg/browser/utils/string';
import Markdown from '@wandb/common/components/Markdown';
import {PopupDropdown} from '@wandb/common/components/PopupDropdown';
import docURLs from '@wandb/common/util/doc_urls';
import {fuzzyMatchWithMapping} from '@wandb/common/util/fuzzyMatch';
import {Link, TargetBlank} from '@wandb/common/util/links';
import {isNotNullOrUndefined} from '@wandb/common/util/types';
import {WBIcon} from '@wandb/ui';
import {Location} from 'history';
import _ from 'lodash';
import moment from 'moment';
import qs from 'query-string';
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useLocation, useRouteMatch} from 'react-router';
import {
  Button,
  Card,
  Container,
  DropdownItemProps,
  Form,
  Header,
  Icon,
  Input,
  Modal,
  StrictInputProps,
} from 'semantic-ui-react';

import {tabletBreakpoint} from '../breakpoints';
import ImageIconHeader from '../components/ImageIconHeader';
import {useHideNavOnPage} from '../components/NavContextProvider';
import ProfileDropdownMenu from '../components/ProfileDropdownMenu';
import BarChart from '../components/vis/BarChart';
import {
  useCreateNewsletterSubscriptionMutation,
  useGalleryQuery,
  useNewsletterSubscriptionQuery,
  useStarViewMutation,
  useSubmitFcProjectMutation,
  useUnstarViewMutation,
} from '../generated/graphql';
import {GALLERY_PATH_SEGMENT} from '../routeData';
import {auth} from '../setup';
import {usePrevious} from '../state/hooks';
import {useViewer} from '../state/viewer/hooks';
import {propagateErrorsContext} from '../util/errors';
import {
  BizzaboEvent,
  DEFAULT_LANGUAGE,
  DISCUSSION_CATEGORY_ID,
  EMPTY_GALLERY_SPEC,
  FCPage,
  FeaturedReportCategory,
  formatGalleryTag,
  GallerySpec,
  getGalleryTagQS,
  isFCPage,
  isLanguage,
  Language,
  LANGUAGES,
  MAIN_PAGE,
  newRepo,
  newRepoLink,
  Repo,
  RepoLink,
  ReportIDWithTagIDs,
  ReportMetadata,
  RepoWithUsage,
  TAG_PAGES,
  TagV2,
  useBizzaboEvents,
  useGallerySpec,
  useProjectUsages,
  useReportMetadata,
  useReportMetadataWithCache,
  useReportTags,
  useTagReportData,
} from '../util/gallery';
import history from '../util/history';
import {
  useDebounceState,
  useIsFirstRender,
  useScrollIntoView,
} from '../util/hooks';
import {parseNameAndIDSafe} from '../util/url';
import * as URLs from '../util/urls';
import {isOneOf} from '../util/utility';
import {useWindowSize} from '../util/window';
import {InstrumentedLoader as Loader} from './../components/utility/InstrumentedLoader';
import * as S from './FC.styles';
import {DropdownTrigger, PostWrapper} from './GalleryPage';
import * as HomePageWithDataS from './HomePageWithData.styles';
import {ReportPageViewFC} from './ReportPageView';

type RouteParams = NormalParams | ReportPageParams | RepoParams | TopicParams;

type NormalParams = {
  page?: string;
  tag?: string;
};

type ReportPageParams = {
  entityName: string;
  projectName: string;
  reportNameAndID: string;
};

type RepoParams = {
  repoName: string;
};

type TopicParams = {
  topic: string;
};

function isReportPageParams(p: RouteParams): p is ReportPageParams {
  return `reportNameAndID` in p;
}

function isRepoParams(p: RouteParams): p is RepoParams {
  return `repoName` in p;
}

function isTopicParams(p: RouteParams): p is TopicParams {
  return `topic` in p;
}

const LANGUAGE_OPTIONS: DropdownItemProps[] = LANGUAGES.map(o => ({
  text: o,
  onClick: () => {
    history.push({
      search: o === DEFAULT_LANGUAGE ? '' : `?language=${o}`,
    });
  },
}));

type ProductsDropdownLink = {
  url: string;
  name: string;
  description: string;
  imageURL: string;
};

const PRODUCTS_DROPDOWN_LINKS: ProductsDropdownLink[] = [
  {
    url: URLs.experimentTracking(),
    name: `Experiments`,
    description: `Lightweight experiment tracking`,
    imageURL: `/fc/nav-icon-experiments.svg`,
  },
  {
    url: URLs.reports(),
    name: `Reports`,
    description: `Collaborative dashboards`,
    imageURL: `/fc/nav-icon-reports.svg`,
  },
  {
    url: URLs.artifacts(),
    name: `Artifacts`,
    description: `Dataset and model versioning`,
    imageURL: `/fc/nav-icon-artifacts.svg`,
  },
  {
    url: URLs.tables(),
    name: `Tables`,
    description: `Interactive data visualization`,
    imageURL: `/fc/nav-icon-tables.svg`,
  },
  {
    url: URLs.sweeps(),
    name: `Sweeps`,
    description: `Hyperparameter optimization`,
    imageURL: `/fc/nav-icon-sweeps.svg`,
  },
];

const PRODUCTS_DROPDOWN_ITEMS: DropdownItemProps[] =
  PRODUCTS_DROPDOWN_LINKS.map(({url, name, description, imageURL}) => ({
    content: (
      <S.TopNavDropdownItem to={url}>
        <S.TopNavDropdownItemImage src={imageURL} />
        <S.TopNavDropdownItemText>
          <b>{name}</b>
          <br />
          {description}
        </S.TopNavDropdownItemText>
      </S.TopNavDropdownItem>
    ),
  }));

type ResourcesDropdownLink = {
  name: string;
} & ({url: string} | {page: FCPage});

const RESOURCES_DROPDOWN_LINKS: ResourcesDropdownLink[] = [
  {
    url: URLs.fullyConnected(),
    name: `Blog`,
  },
  {
    url: URLs.articles(),
    name: `Articles`,
  },
  {
    url: URLs.podcast(),
    name: `Podcast`,
  },
  {
    page: `events`,
    name: `Webinars`,
  },
  {
    url: URLs.communityForum(),
    name: `Community`,
  },
  {
    url: URLs.caseStudies(),
    name: `Case Studies`,
  },
  {
    url: URLs.tutorials(),
    name: `Tutorials`,
  },
  {
    url: URLs.benchmarks(),
    name: `Benchmarks`,
  },
  {
    url: URLs.company(),
    name: `Company`,
  },
  {
    url: URLs.careers(),
    name: `Careers`,
  },
];

type ReportParams = {
  id: string;
  entityName: string;
  projectName?: string;
  displayName: string;
};

type FCContextState = Omit<GallerySpec, 'categories' | 'tags' | 'repos'> &
  Omit<FCReportsResult, 'updateReportMetadata'> &
  StateFromURL & {
    loading: boolean;
    loadingEvents: boolean;
    loadingNewsletterSubscription: boolean;
    currentPage: number;
    minPage: number;
    maxPage: number;
    activeReportParams: ReportParams | null;
    searchQuery: string;
    searchQueryDebounced: string;
    homeCategoryName: string;
    subscribeModalOpen: boolean;
    submitPostModalOpen: boolean;
    submitLinkModalOpen: boolean;
    events: BizzaboEvent[];
    reposWithGitHubMetadata: RepoWithGitHubMetadata[];
    subscribedToNewsletter: boolean;
    reportIDSetByTagName: Map<string, Set<string>>;
    getSetPageLink: (p: FCPage) => string;
    getSetCurrentPageLink: (p: number) => Partial<Location>;
    getSetActiveTagLink: (t: string) => string;
    getSetActiveReportLink: (reportID: string) => string;
    getSetActiveRepoLink: (r: RepoWithUsage) => string;
    getSetActiveTopicLink: (t: TagV2) => string;
    reportBelongsToTag: (reportID: string, tagName: string) => boolean;
  };

const FCContext = React.createContext<FCContextState>({
  ...EMPTY_GALLERY_SPEC,
  loading: false,
  loadingReports: false,
  loadingEvents: false,
  loadingNewsletterSubscription: false,
  page: MAIN_PAGE,
  reports: [],
  newReports: [],
  popularReports: [],
  currentPage: 1,
  minPage: 1,
  maxPage: 1,
  activeTag: ``,
  activeReportID: null,
  activeReportParams: null,
  activeRepo: null,
  activeTopic: null,
  language: DEFAULT_LANGUAGE,
  searchQuery: ``,
  searchQueryDebounced: ``,
  homeCategoryName: ``,
  subscribeModalOpen: false,
  submitPostModalOpen: false,
  submitLinkModalOpen: false,
  events: [],
  reposWithGitHubMetadata: [],
  subscribedToNewsletter: false,
  reportIDSetByTagName: new Map(),
  getSetPageLink: () => ``,
  getSetCurrentPageLink: () => ({}),
  getSetActiveTagLink: () => ``,
  getSetActiveReportLink: () => ``,
  getSetActiveRepoLink: () => ``,
  getSetActiveTopicLink: () => ``,
  reportBelongsToTag: () => false,
});

type FCContextUpdaters = {
  setPage: (page: FCPage) => void;
  setActiveReportID: (id: string) => void;
  setActiveRepo: (repo: string) => void;
  setSearchQuery: (query: string) => void;
  setSearchQueryLiveAndDebounced: (query: string) => void;
  setSubscribeModalOpen: (open: boolean) => void;
  setSubmitPostModalOpen: (open: boolean) => void;
  setSubmitLinkModalOpen: (open: boolean) => void;
  updateReportMetadata: (
    reportID: string,
    updateFn: (r: ReportMetadata) => void
  ) => void;
};

const FCUpdaterContext = React.createContext<FCContextUpdaters>({
  setPage: () => {},
  setActiveReportID: () => {},
  setActiveRepo: () => {},
  setSearchQuery: () => {},
  setSearchQueryLiveAndDebounced: () => {},
  setSubscribeModalOpen: () => {},
  setSubmitPostModalOpen: () => {},
  setSubmitLinkModalOpen: () => {},
  updateReportMetadata: () => {},
});

const FCContextProvider: React.FC = React.memo(({children}) => {
  const viewer = useViewer();
  const {params} = useRouteMatch<RouteParams>();

  const {
    loading: loadingNewsletterSubscription,
    data: newsletterSubscriptionData,
  } = useNewsletterSubscriptionQuery({
    variables: {
      userName: viewer?.username ?? '',
    },
    skip: !viewer?.username,
  });
  const subscribedToNewsletter = useMemo(
    () => newsletterSubscriptionData?.user?.newsletterSubscription != null,
    [newsletterSubscriptionData]
  );

  const {loading, gallerySpec} = useGallerySpec();
  const {tagsV2, reportIDsWithTagV2IDs, repos, eventMetadatas} = gallerySpec;

  const {projectUsages} = useProjectUsages();
  const projectUsageByProjectName = useMemo(
    () => new Map(projectUsages.map(u => [u.projectName, u])),
    [projectUsages]
  );
  const reposWithUsage: RepoWithUsage[] = useMemo(
    () =>
      repos
        .filter(r => !r.pending)
        .map(r => {
          const projectUsage = projectUsageByProjectName.get(r.name);
          return {...r, userCounts: projectUsage?.userCounts ?? []};
        }),
    [repos, projectUsageByProjectName]
  );
  const reposWithGitHubMetadata = useGitHubMetadata(reposWithUsage);

  const {loading: loadingEvents, events} = useBizzaboEvents(eventMetadatas);

  const homeCategoryName = useMemo(
    () => tagsV2.find(t => t.linkForm === '')?.name ?? MAIN_PAGE,
    [tagsV2]
  );

  const {reportIDSetByTagName, reportBelongsToTag} = useTagReportData();

  // Read state from URL
  const {
    page,
    activeTag,
    activeReportID,
    activeRepo,
    activeTopic,
    language,
    currentPage,
  } = deriveStateFromURL({
    params,
    reportIDsWithTagV2IDs,
    tags: tagsV2,
    homeCategoryName,
  });

  const [subscribeModalOpen, setSubscribeModalOpen] = useState(false);
  const [submitPostModalOpen, setSubmitPostModalOpen] = useState(false);
  const [submitLinkModalOpen, setSubmitLinkModalOpen] = useState(false);

  const [
    searchQuery,
    searchQueryDebounced,
    setSearchQuery,
    setSearchQueryLiveAndDebounced,
  ] = useDebounceState('', 300);

  const {reportParams: activeReportParams} =
    useActiveReportParams(activeReportID);

  const {
    loadingReports,
    newReports,
    popularReports,
    reports: allReports,
    updateReportMetadata,
  } = useFCReports({
    reportIDsWithTagV2IDs,
    tagsV2,
    activeTag,
    language,
    searchQueryDebounced,
    reportBelongsToTag,
  });

  const getSetPageLink = useCallback((newPage: FCPage) => {
    if (newPage === MAIN_PAGE) {
      return `/${GALLERY_PATH_SEGMENT}`;
    }
    const pathSegments = [GALLERY_PATH_SEGMENT, newPage];
    return `/${pathSegments.join('/')}`;
  }, []);
  const getSetCurrentPageLink = useCallback((p: number) => {
    const searchObj = qs.parse(history.location.search);

    if (p === 1) {
      delete searchObj.page;
    } else {
      searchObj.page = String(p);
    }

    return {
      pathname: history.location.pathname,
      search: qs.stringify(searchObj),
    };
  }, []);
  const setCurrentPage = useCallback(
    (p: number) => {
      history.push(getSetCurrentPageLink(p));
    },
    [getSetCurrentPageLink]
  );
  const getSetActiveTagLink = useCallback((tagLinkForm: string) => {
    const pathSegments = [GALLERY_PATH_SEGMENT, `blog`, tagLinkForm];
    return `/${pathSegments.join('/')}`;
  }, []);
  const getSetActiveReportLink = useCallback(
    (reportID: string) => {
      const pathSegments = [GALLERY_PATH_SEGMENT, page, reportID];
      return `/${pathSegments.join('/')}`;
    },
    [page]
  );
  const getSetActiveRepoLink = useCallback((repo: RepoWithUsage) => {
    const pathSegments = [GALLERY_PATH_SEGMENT, `projects`, repo.linkForm];
    return `/${pathSegments.join('/')}`;
  }, []);
  const getSetActiveTopicLink = useCallback((topic: TagV2) => {
    const pathSegments = [GALLERY_PATH_SEGMENT, `topics`, topic.linkForm];
    return `/${pathSegments.join('/')}`;
  }, []);

  const setPage = useCallback((newPage: FCPage) => {
    if (newPage === MAIN_PAGE) {
      history.push(`/${GALLERY_PATH_SEGMENT}`);
      return;
    }
    const pathSegments = [GALLERY_PATH_SEGMENT, newPage];
    history.push(`/${pathSegments.join(`/`)}`);
  }, []);
  const setActiveReportID = useCallback((id: string) => {
    // const pathSegments = history.location.pathname.split('/').slice(1);
    // console.log(pathSegments);
    const pathSegments = [GALLERY_PATH_SEGMENT, `blog`, `tag`, id];
    history.push(`/${pathSegments.join(`/`)}`);
  }, []);
  const setActiveRepo = useCallback((name: string) => {
    const pathSegments = [GALLERY_PATH_SEGMENT, `projects`, name];
    history.push(`/${pathSegments.join(`/`)}`);
  }, []);

  const {
    currentItems: reports,
    minPage,
    maxPage,
  } = usePagination({
    loadingItems: loadingReports,
    allItems: allReports,
    itemsPerPage: 20,
    currentPage,
    setCurrentPage,
  });

  useEffect(() => {
    window.analytics?.page(`FC`, {
      fcPage:
        activeReportID != null
          ? `report`
          : activeRepo != null
          ? `project`
          : page,
      reportID: activeReportID,
      project: activeRepo,
    });
  }, [page, activeReportID, activeRepo]);

  const state = useMemo<FCContextState>(
    () => ({
      loading,
      loadingReports,
      loadingEvents,
      loadingNewsletterSubscription,
      ...gallerySpec,
      reports,
      newReports,
      popularReports,
      currentPage,
      minPage,
      maxPage,
      homeCategoryName,
      page,
      activeTag,
      activeReportID,
      activeReportParams,
      activeRepo,
      activeTopic,
      language,
      events,
      reposWithGitHubMetadata,
      searchQuery,
      searchQueryDebounced,
      subscribeModalOpen,
      submitPostModalOpen,
      submitLinkModalOpen,
      subscribedToNewsletter,
      reportIDSetByTagName,
      getSetPageLink,
      getSetCurrentPageLink,
      getSetActiveTagLink,
      getSetActiveReportLink,
      getSetActiveRepoLink,
      getSetActiveTopicLink,
      reportBelongsToTag,
    }),
    [
      loading,
      loadingReports,
      loadingEvents,
      loadingNewsletterSubscription,
      gallerySpec,
      reports,
      newReports,
      popularReports,
      currentPage,
      minPage,
      maxPage,
      homeCategoryName,
      page,
      activeTag,
      activeReportID,
      activeReportParams,
      activeRepo,
      activeTopic,
      language,
      events,
      reposWithGitHubMetadata,
      searchQuery,
      searchQueryDebounced,
      subscribeModalOpen,
      submitPostModalOpen,
      submitLinkModalOpen,
      subscribedToNewsletter,
      reportIDSetByTagName,
      getSetPageLink,
      getSetCurrentPageLink,
      getSetActiveTagLink,
      getSetActiveReportLink,
      getSetActiveRepoLink,
      getSetActiveTopicLink,
      reportBelongsToTag,
    ]
  );

  const updaters = useMemo<FCContextUpdaters>(
    () => ({
      setPage,
      setActiveReportID,
      setActiveRepo,
      setSearchQuery,
      setSearchQueryLiveAndDebounced,
      setSubscribeModalOpen,
      setSubmitPostModalOpen,
      setSubmitLinkModalOpen,
      updateReportMetadata,
    }),
    [
      setPage,
      setActiveReportID,
      setActiveRepo,
      setSearchQuery,
      setSearchQueryLiveAndDebounced,
      updateReportMetadata,
    ]
  );

  return (
    <FCContext.Provider value={state}>
      <FCUpdaterContext.Provider value={updaters}>
        {children}
      </FCUpdaterContext.Provider>
    </FCContext.Provider>
  );
});

function useFCContext(): FCContextState {
  return useContext(FCContext);
}
function useFCContextUpdaters(): FCContextUpdaters {
  return useContext(FCUpdaterContext);
}

const FCWithContext: React.FC = React.memo(() => {
  useHideNavOnPage();
  return (
    <FCContextProvider>
      <FC />
    </FCContextProvider>
  );
});

export default FCWithContext;

type FCProps = {};

const FC: React.FC<FCProps> = React.memo(() => {
  const {
    page,
    activeTag,
    activeReportID,
    activeReportParams,
    activeRepo,
    activeTopic,
  } = useFCContext();

  const isFirstRender = useIsFirstRender();
  const {ref: pageRef, trigger: scrollToTopOfPage} =
    useScrollIntoView<HTMLDivElement>(-S.FC_NAV_HEIGHT);
  useEffect(() => {
    if (!isFirstRender) {
      scrollToTopOfPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, activeTag, activeReportID, activeRepo, activeTopic]);

  return (
    <>
      <S.Page>
        <Desktop>
          <FCTopNav />
        </Desktop>
        <Mobile>{activeReportID == null && <FCTopNav />}</Mobile>
        {activeReportID == null && <FCIntro />}
        <FCNav />

        <S.FCPageContainer ref={pageRef} viewingReport={activeReportID != null}>
          <FCBreadcrumbs />
          <S.FCContent
            wider={activeRepo != null}
            reportMode={activeReportID != null}>
            {activeReportID != null ? (
              activeReportParams != null ? (
                <ReportPageViewFC
                  entityName={activeReportParams.entityName}
                  projectName={activeReportParams.projectName}
                  reportID={activeReportParams.id}
                />
              ) : (
                <LoadingContent />
              )
            ) : activeRepo != null ? (
              <RepoPage />
            ) : isOneOf(page, [`blog`, ...TAG_PAGES]) ? (
              <BlogPage />
            ) : page === `events` ? (
              <EventsPage />
            ) : page === `projects` ? (
              <ReposPage />
            ) : page === `topics` ? (
              <TopicPage />
            ) : null}
          </S.FCContent>
        </S.FCPageContainer>

        <FCCTA />
        <FCFooter />
      </S.Page>

      <SubscribeModal />
      <SubmitPostModal />
      <SubmitLinkModal />
    </>
  );
});

type FCTopNavProps = {};

const FCTopNav: React.FC<FCTopNavProps> = React.memo(() => {
  const viewer = useViewer();
  const {getSetPageLink} = useFCContext();
  const [mobileNavMenuOpen, setMobileNavMenuOpen] = useState(false);

  const resourcesDropdownItems: DropdownItemProps[] = useMemo(
    () =>
      RESOURCES_DROPDOWN_LINKS.map(l => ({
        content: (
          <S.TopNavDropdownItem
            to={`url` in l ? l.url : getSetPageLink(l.page)}>
            {l.name}
          </S.TopNavDropdownItem>
        ),
      })),
    [getSetPageLink]
  );

  return (
    <S.TopNav>
      <S.TopNavContent>
        <S.TopNavLogo to={getSetPageLink(MAIN_PAGE)}>
          <S.TopNavLogoImage src={`/logo-transparent.png`} />
          Weights & Biases
        </S.TopNavLogo>
        <Desktop>
          <S.TopNavDropdown
            on="hover"
            MenuComponent={S.TopNavDropdownMenu}
            options={PRODUCTS_DROPDOWN_ITEMS}
            offset={`0px, -16px`}
            trigger={
              <div>
                <S.TopNavDropdownTrigger>
                  Products
                  <S.TopNavDropdownIcon name="down" />
                </S.TopNavDropdownTrigger>
              </div>
            }
          />
          <S.TopNavDropdown
            on="hover"
            MenuComponent={S.TopNavDropdownMenu}
            options={resourcesDropdownItems}
            offset={`0px, -16px`}
            trigger={
              <div>
                <S.TopNavDropdownTrigger>
                  Resources
                  <S.TopNavDropdownIcon name="down" />
                </S.TopNavDropdownTrigger>
              </div>
            }
          />
          <S.TopNavLink to={docURLs.root}>Docs</S.TopNavLink>
          <S.TopNavLink to={URLs.pricing()}>Pricing</S.TopNavLink>
          <S.TopNavLink to={URLs.enterprise()}>Enterprise</S.TopNavLink>
          {viewer ? (
            <S.ProfileDropdownMenuContainer className={`search-nav`}>
              <ProfileDropdownMenu />
            </S.ProfileDropdownMenuContainer>
          ) : (
            <S.LoginButtons>
              <S.LoginLink to={URLs.login()}>
                <S.LoginLinkIcon name="lock" />
                Login
              </S.LoginLink>
              <S.LoginLink to={URLs.login({signup: true})}>Signup</S.LoginLink>
            </S.LoginButtons>
          )}
        </Desktop>
        <Mobile>
          <S.MobileNavMenuTrigger
            onClick={() => setMobileNavMenuOpen(prev => !prev)}>
            <S.MobileNavMenuTriggerBar />
            <S.MobileNavMenuTriggerBar />
            <S.MobileNavMenuTriggerBar />
            <S.MobileNavMenu
              active={mobileNavMenuOpen}
              onClick={e => e.stopPropagation()}>
              {viewer == null && (
                <>
                  <S.MobileNavLink to={URLs.login()}>Login</S.MobileNavLink>
                  <S.MobileNavLink to={URLs.login({signup: true})}>
                    Signup
                  </S.MobileNavLink>
                </>
              )}
              <S.MobileNavDropdownTrigger>
                Products{' '}
                <S.MobileNavDropdownTriggerIcon name={`chevron-down`} />
              </S.MobileNavDropdownTrigger>
              <S.MobileNavDropdown>
                {PRODUCTS_DROPDOWN_LINKS.map(
                  ({url, name, description, imageURL}, i) => (
                    <S.TopNavDropdownItem withPadding key={i} to={url}>
                      <S.TopNavDropdownItemImage src={imageURL} />
                      <S.TopNavDropdownItemText>
                        <b>{name}</b>
                        <br />
                        {description}
                      </S.TopNavDropdownItemText>
                    </S.TopNavDropdownItem>
                  )
                )}
              </S.MobileNavDropdown>
              <S.MobileNavDropdownTrigger>
                Resources{' '}
                <S.MobileNavDropdownTriggerIcon name={`chevron-down`} />
              </S.MobileNavDropdownTrigger>
              <S.MobileNavDropdown>
                {RESOURCES_DROPDOWN_LINKS.map((l, i) => (
                  <S.MobileNavDropdownLink
                    key={i}
                    to={`url` in l ? l.url : getSetPageLink(l.page)}>
                    {l.name}
                  </S.MobileNavDropdownLink>
                ))}
              </S.MobileNavDropdown>
              <S.MobileNavLink to={docURLs.root}>Docs</S.MobileNavLink>
              <S.MobileNavLink to={URLs.pricing()}>Pricing</S.MobileNavLink>
              <S.MobileNavLink to={URLs.enterprise()}>
                Enterprise
              </S.MobileNavLink>
            </S.MobileNavMenu>
          </S.MobileNavMenuTrigger>
        </Mobile>
      </S.TopNavContent>
    </S.TopNav>
  );
});

type FCIntroProps = {};

const FCIntro: React.FC<FCIntroProps> = React.memo(() => {
  const viewer = useViewer();
  const {
    page,
    activeRepo,
    subscribedToNewsletter,
    loadingNewsletterSubscription,
  } = useFCContext();
  const {
    setSubscribeModalOpen,
    setSubmitPostModalOpen,
    setSubmitLinkModalOpen,
  } = useFCContextUpdaters();
  return (
    <S.Intro>
      <S.IntroContent>
        {page === `blog` ? (
          <>
            <S.IntroHeader>W&B Fully Connected</S.IntroHeader>
            <S.IntroBody>Home of the applied ML community</S.IntroBody>
          </>
        ) : page === `ml-news` ? (
          <>
            <S.IntroHeader>W&B Fully Connected</S.IntroHeader>
            <S.IntroBody>
              Whether it’s new research, hardware, techniques, or industry
              trends, this is your place to find the freshest news in machine
              learning.
            </S.IntroBody>
          </>
        ) : page === `podcast` ? (
          <>
            <S.IntroHeader>W&B Fully Connected</S.IntroHeader>
            <S.IntroBody>
              All the episodes of our biweekly podcast, Gradient Dissent. Join
              our founder Lukas Biewald as he interviews ML leaders from the
              most innovative companies and research institutions in the world.
            </S.IntroBody>
          </>
        ) : page === `projects` ? (
          <>
            <S.IntroHeader>W&B Fully Connected</S.IntroHeader>
            <S.IntroBody>
              Our home for the most popular ML frameworks, repos, and projects.
              If you want to see your project on the list, submit it via the
              link below!
            </S.IntroBody>
          </>
        ) : page === `events` ? (
          <>
            <S.IntroHeader>Upcoming W&B Events</S.IntroHeader>
            <S.IntroBody>
              Learn about our upcoming webinars, paper reading groups, AMAs,
              conferences, & more! Our past events are available on demand too.
            </S.IntroBody>
          </>
        ) : null}
        <S.IntroButtons>
          <S.IntroButtonImg
            src={`/fc/button-${
              activeRepo != null
                ? `share-link`
                : page === `projects`
                ? `share-project`
                : `write-post`
            }.png`}
            onClick={() => {
              if (page !== `projects` && activeRepo == null) {
                setSubmitPostModalOpen(true);
                return;
              }
              if (viewer == null) {
                auth.login();
                return;
              }
              setSubmitLinkModalOpen(true);
            }}
          />
          {!subscribedToNewsletter && !loadingNewsletterSubscription && (
            <S.IntroButtonImg
              src={`/fc/button-subscribe.png`}
              onClick={() => setSubscribeModalOpen(true)}
            />
          )}
        </S.IntroButtons>
      </S.IntroContent>
    </S.Intro>
  );
});

type FCNavLink = {
  label: string;
} & ({page: FCPage} | {url: string});

const FC_NAV_LINKS: FCNavLink[] = [
  {page: `blog`, label: `Articles`},
  {page: `projects`, label: `Projects`},
  {page: `ml-news`, label: `ML News`},
  {page: `events`, label: `Events`},
  {page: `podcast`, label: `Podcast`},
  {url: URLs.communityForum(), label: `Forum`},
  // {page: `learn-ml`, label: `Learn ML`},
];

function getLabelForFCPage(page: FCPage): string {
  return FC_NAV_LINKS.find(l => `page` in l && l.page === page)?.label ?? page;
}

type FCNavProps = {};

const FCNav: React.FC<FCNavProps> = React.memo(() => {
  const {page, getSetPageLink} = useFCContext();

  const [mobileNavMenuOpen, setMobileNavMenuOpen] = useState(false);

  return (
    <>
      <Desktop>
        <S.FCNav>
          <S.FCNavContent>
            {FC_NAV_LINKS.map((l, i) => (
              <S.FCNavLink
                key={i}
                to={`page` in l ? getSetPageLink(l.page) : l.url}
                onClick={() => {
                  trackClickPage(l, `FC nav`);
                }}
                active={`page` in l && page === l.page}>
                {l.label}
              </S.FCNavLink>
            ))}
          </S.FCNavContent>
        </S.FCNav>
      </Desktop>
      <Mobile>
        <S.MobileNav>
          <S.MobileNavText>Weights & Biases</S.MobileNavText>
          <S.MobileNavMenuTrigger
            onClick={() => setMobileNavMenuOpen(prev => !prev)}>
            <S.MobileNavMenuTriggerBar />
            <S.MobileNavMenuTriggerBar />
            <S.MobileNavMenuTriggerBar />
            <S.MobileNavMenu
              active={mobileNavMenuOpen}
              onClick={e => e.stopPropagation()}>
              {FC_NAV_LINKS.map((l, i) => (
                <S.MobileNavLink
                  key={i}
                  to={`page` in l ? getSetPageLink(l.page) : l.url}
                  onClick={() => {
                    trackClickPage(l, `FC mobile pages nav menu`);
                  }}>
                  {l.label}
                </S.MobileNavLink>
              ))}
            </S.MobileNavMenu>
          </S.MobileNavMenuTrigger>
        </S.MobileNav>
      </Mobile>
    </>
  );

  function trackClickPage(l: FCNavLink, location: string): void {
    window.analytics?.track(`FC Page Clicked`, {
      location,
      name: l.label,
    });
  }
});

type FCCTAProps = {};

const FCCTA: React.FC<FCCTAProps> = React.memo(() => {
  const viewer = useViewer();
  return (
    <S.CTA>
      <S.CTAContent>
        <S.CTAText>
          Never lose track of
          <ResponsiveSpace />
          another ML project.
          <ResponsiveSpace />
          <S.GoldText>
            Try <Responsive desktop={`W&B`} mobile={`Weights & Biases`} />{' '}
            today.
          </S.GoldText>
        </S.CTAText>
        <S.CTAButtons>
          {viewer == null && (
            <S.CTAButtonGold data-test="signup" to={URLs.login({signup: true})}>
              Sign Up
            </S.CTAButtonGold>
          )}
          <S.CTAButtonWhite to={docURLs.gettingStarted}>
            Try W&B Now
          </S.CTAButtonWhite>
        </S.CTAButtons>
      </S.CTAContent>
    </S.CTA>
  );
});

// type FCFooterSocialLink = {
//   url: string;
//   icon: string;
// };

// const FOOTER_SOCIAL_LINKS: FCFooterSocialLink[] = [
//   {url: URLs.facebook(), icon: ``},
//   {url: URLs.instagram(), icon: ``},
//   {url: URLs.twitter(), icon: ``},
//   {url: URLs.youtube(), icon: ``},
// ];

type FCFooterNavSection = {
  header: string;
  links: FCFooterNavLink[];
};

type FCFooterNavLink = {
  url: string;
  text: string;
  mobileOnly?: true;
};

const FOOTER_NAV_SECTIONS: FCFooterNavSection[] = [
  {
    header: `Products`,
    links: [
      {url: URLs.experimentTracking(), text: `Dashboard`},
      {url: URLs.sweeps(), text: `Sweeps`},
      {url: URLs.artifacts(), text: `Artifacts`},
      {url: URLs.reports(), text: `Reports`},
    ],
  },
  {header: `Quickstart`, links: [{url: docURLs.root, text: `Documentation`}]},
  {
    header: `Resources`,
    links: [
      {url: URLs.communityForum(), text: `Forum`},
      {url: URLs.tutorials(), text: `Tutorials`},
      {url: URLs.benchmarks(), text: `Benchmarks`},
    ],
  },
  {
    header: `W&B`,
    links: [
      {url: URLs.company(), text: `About Us`},
      {url: URLs.authors(), text: `Authors`},
      {url: URLs.contactSales(), text: `Contact`},
      {url: URLs.termsOfService(), text: `Terms of Service`, mobileOnly: true},
      {url: URLs.privacyPolicy(), text: `Privacy Policy`, mobileOnly: true},
    ],
  },
];

type FCFooterProps = {};

const FCFooter: React.FC<FCFooterProps> = React.memo(() => {
  const {subscribedToNewsletter, loadingNewsletterSubscription} =
    useFCContext();
  const {setSubscribeModalOpen} = useFCContextUpdaters();
  return (
    <S.Footer>
      <S.FooterContent>
        <S.FooterMain>
          <S.FooterLeftColumn>
            <S.FooterLogo>
              <S.FooterLogoImage src={`/logo-transparent.png`} />
              Weights & Biases
            </S.FooterLogo>
            {/* <S.FooterSocials>
              <S.FooterSocialText>Stay Connected</S.FooterSocialText>
              <S.FooterSocialLinks>
                {FOOTER_SOCIAL_LINKS.map(({url, icon}, i) => (
                  <S.FooterSocialLink key={i} to={url}></S.FooterSocialLink>
                ))}
              </S.FooterSocialLinks>
            </S.FooterSocials> */}
            {!subscribedToNewsletter && !loadingNewsletterSubscription && (
              <S.FooterSubscribe>
                Get weekly updates with the latest ML news.
                <S.FooterSubscribeButton
                  onClick={() => setSubscribeModalOpen(true)}>
                  Subscribe
                </S.FooterSubscribeButton>
              </S.FooterSubscribe>
            )}
          </S.FooterLeftColumn>
          <S.FooterNav>
            {FOOTER_NAV_SECTIONS.map(({header, links}, i) => (
              <S.FooterNavColumn key={i}>
                <S.FooterNavColumnHeader>{header}</S.FooterNavColumnHeader>
                <S.FooterNavColumnLinks>
                  {links.map(({url, text, mobileOnly}, j) => (
                    <React.Fragment key={j}>
                      {j > 0 && <S.FooterNavLinkDivider />}
                      <S.FooterNavColumnLink
                        key={j}
                        to={url}
                        mobileOnly={mobileOnly}>
                        {text}
                      </S.FooterNavColumnLink>
                    </React.Fragment>
                  ))}
                </S.FooterNavColumnLinks>
              </S.FooterNavColumn>
            ))}
          </S.FooterNav>
        </S.FooterMain>
        <S.FooterFooter>
          <S.FooterFooterContent>
            <S.FooterFooterLeftColumn>
              Copyright &copy;{new Date().getFullYear()} Weights & Biases. All
              rights reserved.
            </S.FooterFooterLeftColumn>
            <S.FooterFooterRightColumn>
              <S.FooterFooterLink to={URLs.termsOfService()}>
                Terms of Service
              </S.FooterFooterLink>
              <S.FooterFooterLink to={URLs.privacyPolicy()}>
                Privacy Policy
              </S.FooterFooterLink>
            </S.FooterFooterRightColumn>
          </S.FooterFooterContent>
        </S.FooterFooter>
      </S.FooterContent>
    </S.Footer>
  );
});

type FCBreadcrumbSegment = {
  text: string;
  linkTo?: string;
};

type FCBreadcrumbsProps = {};

const FCBreadcrumbs: React.FC<FCBreadcrumbsProps> = React.memo(() => {
  const {
    page,
    activeReportParams,
    activeRepo,
    getSetPageLink,
    getSetActiveTagLink,
  } = useFCContext();
  const {primaryTag} = useReportTags(activeReportParams?.id ?? null);

  const segments: FCBreadcrumbSegment[] = useMemo(() => {
    if (activeReportParams != null) {
      return [
        {text: getLabelForFCPage(page), linkTo: getSetPageLink(page)},
        ...(primaryTag != null
          ? [
              {
                text: primaryTag.name,
                linkTo: getSetActiveTagLink(primaryTag.linkForm),
              },
            ]
          : []),
        {text: activeReportParams.displayName},
      ];
    }
    if (activeRepo != null) {
      return [
        {text: getLabelForFCPage(page), linkTo: getSetPageLink(page)},
        {text: activeRepo},
      ];
    }
    return [];
  }, [
    page,
    activeReportParams,
    activeRepo,
    getSetPageLink,
    getSetActiveTagLink,
    primaryTag,
  ]);

  const width =
    activeReportParams != null
      ? 1072
      : activeRepo != null
      ? 1280
      : S.MAX_PAGE_WIDTH;

  if (segments.length === 0) {
    return null;
  }

  return (
    <S.Breadcrumbs width={width}>
      <S.BreadcrumbsLink to={URLs.fullyConnected()}>
        W&B Fully Connected
      </S.BreadcrumbsLink>
      {segments.map(({text, linkTo}, i) => (
        <React.Fragment key={i}>
          <S.BreadcrumbsSeparator>{`>`}</S.BreadcrumbsSeparator>
          {linkTo ? (
            <S.BreadcrumbsLink to={linkTo}>{text}</S.BreadcrumbsLink>
          ) : (
            <S.BreadcrumbsSpan>{text}</S.BreadcrumbsSpan>
          )}
        </React.Fragment>
      ))}
    </S.Breadcrumbs>
  );
});

const POPULAR_TOPICS = [`tasks`, `framework--integration`, `domain`];

type BlogPageProps = {};

const BlogPage: React.FC<BlogPageProps> = React.memo(() => {
  const viewer = useViewer();
  const [activeFeaturedCategory, setActiveFeaturedCategory] =
    useState<FeaturedReportCategory>(`new`);
  const [mobileNavMenuOpen, setMobileNavMenuOpen] = useState(false);
  const [mobileEventIndex, setMobileEventIndex] = useState(0);

  const {
    page,
    activeTag,
    tagsV2,
    language,
    searchQuery,
    events,
    currentPage,
    reports,
    newReports,
    popularReports,
    loadingReports,
  } = useFCContext();
  const {setSearchQuery, setSearchQueryLiveAndDebounced, updateReportMetadata} =
    useFCContextUpdaters();

  const prevPage = usePrevious(currentPage);
  const {ref: reportsHeaderRef, trigger: scrollToReportsHeader} =
    useScrollIntoView<HTMLHeadingElement>();
  useEffect(() => {
    if (prevPage != null && currentPage !== prevPage) {
      scrollToReportsHeader();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const [starViewMutation] = useStarViewMutation();
  const [unstarViewMutation] = useUnstarViewMutation();
  const star = useCallback(
    async (viewID: string) => {
      if (viewer == null) {
        auth.login();
        return;
      }
      updateReportMetadata(viewID, r => (r.starred = true));
      return starViewMutation({variables: {id: viewID}});
    },
    [viewer, updateReportMetadata, starViewMutation]
  );
  const unstar = useCallback(
    async (viewID: string) => {
      if (viewer == null) {
        auth.login();
        return;
      }
      updateReportMetadata(viewID, r => (r.starred = false));
      return unstarViewMutation({variables: {id: viewID}});
    },
    [viewer, updateReportMetadata, unstarViewMutation]
  );

  const renderReport = useCallback(
    (r: ReportMetadata, hidden: boolean) => {
      const link =
        (r.project != null
          ? URLs.reportView({
              entityName: r.project.entityName,
              projectName: r.project.name,
              reportID: r.id,
              reportName: r.displayName,
            })
          : (r.tags?.some(t => t.id === DISCUSSION_CATEGORY_ID)
              ? URLs.galleryDiscussionView
              : URLs.galleryPostView)({
              entityName: r.entityName,
              reportID: r.id,
              reportName: r.displayName,
            })) + getGalleryTagQS(tagsV2, activeTag);
      return (
        <S.PostWrapperWrapper key={r.id} hidden={hidden}>
          <S.PostHeart
            name={r.starred ? 'heart' : 'heart-outline'}
            onClick={() => (r.starred ? unstar(r.id) : star(r.id))}
          />
          <PostWrapper
            {...r}
            link={link}
            activeTag={''}
            tagIsCategory={() => false}
            showDescription={false}
            loading={false}
          />
        </S.PostWrapperWrapper>
      );
    },
    [activeTag, star, tagsV2, unstar]
  );

  const futureEvents = useMemo(() => {
    return _.sortBy(
      events.filter(e => new Date(e.startDate).valueOf() > Date.now()),
      e => new Date(e.startDate).valueOf()
    );
  }, [events]);

  const isBlogPage = page === `blog`;

  return (
    <S.Columns>
      <S.LeftColumn>
        <Mobile>
          {futureEvents.length > 0 && (
            <>
              <S.SectionHeader>Upcoming Events</S.SectionHeader>
              <S.MobileEvents>
                <S.MobileEventsLeft
                  hidden={mobileEventIndex <= 0}
                  onClick={() => setMobileEventIndex(prev => prev - 1)}
                />
                {futureEvents
                  .slice(mobileEventIndex, mobileEventIndex + 1)
                  .map(e => (
                    <S.BlogPageEvent key={e.id} to={e.url} noMargin>
                      <S.BlogPageEventTime>
                        {new Date(e.startDate).toDateString()}
                      </S.BlogPageEventTime>
                      <S.BlogPageEventName>{e.name}</S.BlogPageEventName>
                      <S.BlogPageEventImage
                        src={e.fcMetadata?.imageURL ?? `/logo-transparent.png`}
                      />
                    </S.BlogPageEvent>
                  ))}
                <S.MobileEventsRight
                  hidden={mobileEventIndex >= futureEvents.length - 1}
                  onClick={() => setMobileEventIndex(prev => prev + 1)}
                />
              </S.MobileEvents>
            </>
          )}
          {isBlogPage && <S.SectionHeader>Featured ML Posts</S.SectionHeader>}
        </Mobile>
        {newReports.length > 0 || popularReports.length > 0 ? (
          <>
            <S.SectionPicker>
              <S.SectionPickerItem
                active={activeFeaturedCategory === `new`}
                onClick={() => setActiveFeaturedCategory(`new`)}>
                <S.SectionHeader>New</S.SectionHeader>
                <S.SectionPickerIcon src={`/fc/icon-star.png`} />
              </S.SectionPickerItem>
              <S.SectionPickerItem
                active={activeFeaturedCategory === `popular`}
                onClick={() => setActiveFeaturedCategory(`popular`)}>
                <S.SectionHeader>Popular</S.SectionHeader>
                <S.SectionPickerIcon src={`/fc/icon-fire.png`} />
              </S.SectionPickerItem>
            </S.SectionPicker>
            <S.Section>
              {(activeFeaturedCategory === `new`
                ? newReports
                : popularReports
              ).map(r => renderReport(r, false))}
              {(activeFeaturedCategory === `new`
                ? popularReports
                : newReports
              ).map(r => renderReport(r, true))}
            </S.Section>
          </>
        ) : loadingReports ? (
          <LoadingContent />
        ) : null}
        <Desktop>
          <S.Section smallPadding ref={reportsHeaderRef}>
            <S.Search>
              <S.SearchIcon name="search" />
              <S.SearchInput
                placeholder="Search posts"
                value={searchQuery}
                onChange={
                  ((e, {value}) =>
                    setSearchQuery(value)) as StrictInputProps['onChange']
                }
              />
              <S.ClearSearchButton
                onClick={() => setSearchQueryLiveAndDebounced('')}>
                Clear Search
              </S.ClearSearchButton>
              <PopupDropdown
                options={LANGUAGE_OPTIONS}
                offset={`0px, -16px`}
                trigger={
                  <div>
                    <DropdownTrigger width={100} value={language} />
                  </div>
                }
              />
            </S.Search>
          </S.Section>
        </Desktop>
        {isBlogPage && (
          <Mobile>
            <S.MobileNav insideContainer>
              <S.MobileNavText>Popular Topics</S.MobileNavText>
              <S.MobileNavMenuTrigger
                onClick={() => setMobileNavMenuOpen(prev => !prev)}>
                <S.MobileNavMenuTriggerBar />
                <S.MobileNavMenuTriggerBar />
                <S.MobileNavMenuTriggerBar />
                <S.MobileNavMenu
                  active={mobileNavMenuOpen}
                  onClick={e => e.stopPropagation()}>
                  <S.MobileNavSearch
                    placeholder="Search posts"
                    value={searchQuery}
                    onChange={
                      ((e, {value}) =>
                        setSearchQuery(value)) as StrictInputProps['onChange']
                    }
                  />
                  <S.MobileNavDropdown>
                    {POPULAR_TOPICS.map(topicLinkForm => (
                      <PopularTopic
                        key={topicLinkForm}
                        topicLinkForm={topicLinkForm}
                      />
                    ))}
                  </S.MobileNavDropdown>
                </S.MobileNavMenu>
              </S.MobileNavMenuTrigger>
            </S.MobileNav>
          </Mobile>
        )}
        {loadingReports ? (
          <LoadingContent />
        ) : (
          <>
            <S.Section>{reports.map(r => renderReport(r, false))}</S.Section>
            <Pagination />
          </>
        )}
      </S.LeftColumn>

      <S.RightColumn>
        {futureEvents.length > 0 && (
          <>
            <S.SectionHeader>Upcoming Events</S.SectionHeader>
            <S.Section extraPadding>
              {futureEvents.slice(0, 5).map(e => (
                <S.BlogPageEvent key={e.id} to={e.url}>
                  <S.BlogPageEventTime>
                    {new Date(e.startDate).toDateString()}
                  </S.BlogPageEventTime>
                  <S.BlogPageEventName>{e.name}</S.BlogPageEventName>
                  <S.BlogPageEventImage
                    src={e.fcMetadata?.imageURL ?? `/logo-transparent.png`}
                  />
                </S.BlogPageEvent>
              ))}
            </S.Section>
          </>
        )}
        {isBlogPage && (
          <>
            <S.SectionHeader>Popular Topics</S.SectionHeader>
            {POPULAR_TOPICS.map(topicLinkForm => (
              <PopularTopic key={topicLinkForm} topicLinkForm={topicLinkForm} />
            ))}
          </>
        )}
      </S.RightColumn>
    </S.Columns>
  );
});

type PopularTopicProps = {topicLinkForm: string};

const PopularTopic: React.FC<PopularTopicProps> = React.memo(
  ({topicLinkForm}) => {
    const {
      activeTag,
      tagsV2,
      reportIDSetByTagName,
      getSetActiveTagLink,
      getSetActiveTopicLink,
    } = useFCContext();

    const topic = tagsV2.find(t => t.linkForm === topicLinkForm);
    if (topic == null) {
      return null;
    }

    const topicTags = _.sortBy(
      tagsV2.filter(t => t.parent === topic.id && !t.hidden),
      t => -(reportIDSetByTagName.get(t.name)?.size ?? 0)
    );
    if (topicTags.length === 0) {
      return null;
    }

    return (
      <>
        <Desktop>
          <S.Section smallMargin>
            <S.BlogPageListHeader>{topic.name}</S.BlogPageListHeader>
            {topicTags.slice(0, 5).map(t => (
              <S.BlogPageListItem
                key={t.id}
                to={getSetActiveTagLink(t.linkForm)}
                onClick={() => {
                  trackClickTag(t, `FC right nav bar`);
                }}
                active={activeTag === t.name}>
                {t.name}
              </S.BlogPageListItem>
            ))}
            <S.BlogPageListItem to={getSetActiveTopicLink(topic)}>
              All
            </S.BlogPageListItem>
          </S.Section>
        </Desktop>
        <Mobile>
          <S.MobileNavDropdownTrigger>
            {topic.name}{' '}
            <S.MobileNavDropdownTriggerIcon name={`chevron-down`} />
          </S.MobileNavDropdownTrigger>
          {topicTags.slice(0, 5).map(t => (
            <S.MobileNavDropdownLink
              key={t.id}
              to={getSetActiveTagLink(t.linkForm)}
              onClick={() => {
                trackClickTag(t, `FC mobile tags nav menu`);
              }}>
              {t.name}
            </S.MobileNavDropdownLink>
          ))}
          <S.MobileNavDropdownLink to={getSetActiveTopicLink(topic)}>
            All
          </S.MobileNavDropdownLink>
        </Mobile>
      </>
    );

    function trackClickTag(t: TagV2, location: string): void {
      window.analytics?.track(`FC Tag Clicked`, {
        location,
        tagID: t.id,
        tagName: t.name,
      });
    }
  }
);

const EVENT_PAGE_SIZE = 9;

type EventsPageProps = {};

const EventsPage: React.FC<EventsPageProps> = React.memo(() => {
  const {
    loadingEvents,
    events,
    subscribedToNewsletter,
    loadingNewsletterSubscription,
  } = useFCContext();
  const {setSubscribeModalOpen} = useFCContextUpdaters();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [startDateFilter, setStartDateFilter] = useState<Date | null>(null);
  const [endDateFilter, setEndDateFilter] = useState<Date | null>(null);
  const [region] = useState<string>('');
  const [topic] = useState<string>('');

  const [fcEvents, setFCEvents] = useState(events);
  useEffect(() => {
    if (!loadingEvents) {
      setFCEvents(events);
    }
  }, [loadingEvents, events]);

  const [pastEventsShown, setPastEventsShown] = useState(EVENT_PAGE_SIZE);
  const [futureEventsShown, setFutureEventsShown] = useState(EVENT_PAGE_SIZE);
  const [pastEvents, futureEvents] = useMemo(() => {
    return [
      fcEvents.filter(e => new Date(e.startDate).valueOf() <= Date.now()),
      fcEvents.filter(e => new Date(e.startDate).valueOf() > Date.now()),
    ];
  }, [fcEvents]);
  const showPastShowMoreButton = pastEventsShown < pastEvents.length;
  const showFutureShowMoreButton = futureEventsShown < futureEvents.length;

  // REGION
  const regionOptions = useMemo(() => {
    const allRegions = _.uniq(
      _.flatten(_.compact(events.map(e => e.timezone.split('/')[0])))
    );
    const regionOpts = allRegions.map(r => ({
      text: r,
      value: r.toLowerCase(),
    }));
    const allOpts = [{text: '-- Region --', value: ''}, ...regionOpts];
    return allOpts;
  }, [events]);
  const regionValToText = useMemo(() => {
    const regionMap = new Map<string, string>();
    regionOptions.forEach(o => regionMap.set(o.value, o.text));
    return regionMap;
  }, [regionOptions]);

  // TOPIC
  const topicOptions = useMemo(() => {
    const allTopics = _.uniq(_.flatten(_.compact(events.map(e => e.type))));
    const topicOpts = allTopics.map(t => ({
      text: t,
      value: t.toLowerCase().replace(/&/g, 'and').replace(/ /g, '-'),
    }));
    const allOpts = [{text: '-- Topic --', value: ''}, ...topicOpts];
    return allOpts;
  }, [events]);
  const topicValToText = useMemo(() => {
    const topicMap = new Map<string, string>();
    topicOptions.forEach(o => topicMap.set(o.value, o.text));
    return topicMap;
  }, [topicOptions]);

  // SEARCH
  const searchEvents = useCallback(() => {
    let results = events;
    // query search
    if (searchQuery !== '') {
      results = fuzzyMatchWithMapping(results, searchQuery, e => e.name);
    }
    // from date
    if (startDateFilter != null) {
      results = results.filter(e => {
        const eventStart = new Date(e.startDate);
        return eventStart >= startDateFilter;
      });
    }
    // to date
    if (endDateFilter != null) {
      results = results.filter(e => {
        const eventStart = new Date(e.startDate);
        return eventStart <= endDateFilter;
      });
    }
    // region of choice
    if (region !== '') {
      results = results.filter(e =>
        e.timezone.includes(regionValToText.get(region) ?? '')
      );
    }
    // topic of choice
    if (topic !== '') {
      results = results.filter(e =>
        e.type?.includes(topicValToText.get(topic) ?? '')
      );
    }
    setFCEvents(results);
    setPastEventsShown(EVENT_PAGE_SIZE);
    setFutureEventsShown(EVENT_PAGE_SIZE);
  }, [
    events,
    regionValToText,
    topicValToText,
    searchQuery,
    startDateFilter,
    endDateFilter,
    region,
    topic,
  ]);

  useEffect(() => {
    searchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, startDateFilter, endDateFilter]);

  return (
    <>
      <S.EventsContainer>
        <S.EventsControls>
          <S.EventsControlsInput
            placeholder={`Search events`}
            icon={<S.EventsControlsIcon name="search" />}
            value={searchQuery}
            onChange={
              ((e, {value}) =>
                setSearchQuery(value)) as StrictInputProps['onChange']
            }
          />
          <S.EventsControlsDatetimeContainer>
            <S.EventsControlsDatetime
              inputProps={{placeholder: `Select from date`}}
              timeFormat={false}
              value={startDateFilter ?? undefined}
              onChange={d => {
                setStartDateFilter(moment.isMoment(d) ? d.toDate() : null);
              }}
            />
            <S.EventsControlsIcon name="date" />
          </S.EventsControlsDatetimeContainer>
          <S.EventsControlsDatetimeContainer>
            <S.EventsControlsDatetime
              inputProps={{placeholder: `Select to date`}}
              timeFormat={false}
              value={endDateFilter ?? undefined}
              onChange={d => {
                setEndDateFilter(moment.isMoment(d) ? d.toDate() : null);
              }}
            />
            <S.EventsControlsIcon name="date" />
          </S.EventsControlsDatetimeContainer>
          {/* <S.EventsControlsDropdown
            selection
            placeholder={`-- Region --`}
            icon={<S.EventsControlsIcon name="chevron-down" />}
            options={regionOptions}
            value={region}
            onChange={
              ((e, {value}) =>
                setRegion(value as string)) as StrictDropdownProps['onChange']
            }
          />
          <S.EventsControlsDropdown
            selection
            placeholder={`-- Topic --`}
            icon={<S.EventsControlsIcon name="chevron-down" />}
            options={topicOptions}
            value={topic}
            onChange={
              ((e, {value}) =>
                setTopic(value as string)) as StrictDropdownProps['onChange']
            }
          />
          <S.EventsButton onClick={searchEvents}>Search</S.EventsButton> */}
        </S.EventsControls>
        {loadingEvents ? (
          <LoadingContent />
        ) : (
          <>
            {futureEvents.length > 0 && (
              <S.EventsCollection>
                <S.EventsHeader>Upcoming events</S.EventsHeader>
                <S.EventsText>
                  Check out our upcoming webinars, paper reading groups,
                  tutorials, and more! We host multiple events a week and
                  they’re always free for our community. Hope to see you there!
                </S.EventsText>
                <S.EventEntries>
                  {futureEvents
                    .slice(0, futureEventsShown)
                    .map(({url, startDate, name, fcMetadata}, i) => (
                      <S.EventEntry key={i} href={url}>
                        <S.EventEntryImage
                          src={fcMetadata?.imageURL || `/logo.png`}
                        />
                        <S.EventEntryDate>
                          {new Date(startDate).toDateString()}
                        </S.EventEntryDate>
                        <S.EventEntryName>{name}</S.EventEntryName>
                      </S.EventEntry>
                    ))}
                </S.EventEntries>
                <S.ShowMoreContainer>
                  {showFutureShowMoreButton && (
                    <S.ShowMoreButton
                      onClick={() =>
                        setFutureEventsShown(prev => prev + EVENT_PAGE_SIZE)
                      }>
                      Show more
                    </S.ShowMoreButton>
                  )}
                </S.ShowMoreContainer>
              </S.EventsCollection>
            )}
            {pastEvents.length > 0 && (
              <S.EventsCollection>
                <S.EventsHeader>Rewatch past events</S.EventsHeader>
                <S.EventsText>
                  All our previous events are free to watch. Check out anything
                  you’ve missed in the past couple months.
                </S.EventsText>
                <S.EventEntries>
                  {pastEvents
                    .slice(0, pastEventsShown)
                    .map(({url, startDate, name, fcMetadata}, i) => (
                      <S.EventEntry key={i} href={url}>
                        <S.EventEntryImage
                          src={fcMetadata?.imageURL || `/logo.png`}
                        />
                        <S.EventEntryName>{name}</S.EventEntryName>
                      </S.EventEntry>
                    ))}
                </S.EventEntries>
                <S.ShowMoreContainer>
                  {showPastShowMoreButton && (
                    <S.ShowMoreButton
                      onClick={() =>
                        setPastEventsShown(prev => prev + EVENT_PAGE_SIZE)
                      }>
                      Show more
                    </S.ShowMoreButton>
                  )}
                </S.ShowMoreContainer>
              </S.EventsCollection>
            )}
          </>
        )}
      </S.EventsContainer>
      {!subscribedToNewsletter && !loadingNewsletterSubscription && (
        <S.EventsCTA>
          <S.EventsCTAText>
            Don't want to miss any upcoming W&B Events?
            <br />
            Subscribe to reminders!
          </S.EventsCTAText>
          <S.IntroButtonImg
            src={`/fc/button-subscribe.png`}
            onClick={() => setSubscribeModalOpen(true)}
          />
        </S.EventsCTA>
      )}
    </>
  );
});

type ReposPageProps = {};

const ReposPage: React.FC<ReposPageProps> = React.memo(() => {
  const {loading, reposWithGitHubMetadata: repos} = useFCContext();
  const [reposSearch, setReposSearch] = useState('');

  const filteredRepos = useMemo(() => {
    if (reposSearch === '') {
      return repos;
    }
    return fuzzyMatchWithMapping(repos, reposSearch, e =>
      [e.url, e.name, e.description].join(' ')
    );
  }, [reposSearch, repos]);
  return (
    <Container>
      <Header style={{textAlign: 'center'}} size="huge">
        Trending Projects
        <Header.Subheader>
          Browse popular machine learning projects
        </Header.Subheader>
      </Header>
      <div style={{paddingBottom: 10}}>
        <Input
          icon="search"
          value={reposSearch}
          placeholder="Search..."
          onChange={(e, {value}) => {
            setReposSearch(value);
          }}
        />
      </div>
      {loading ? <Loader name="repos-page" /> : <Repos rows={filteredRepos} />}
    </Container>
  );
});

type ReposProps = {
  rows: RepoWithGitHubMetadata[];
};

const Repos: React.FC<ReposProps> = React.memo(({rows}) => {
  const {getSetActiveRepoLink} = useFCContext();
  return (
    <S.RepoList>
      {rows.map(repo => {
        const {id, name, description, stars, forks, imageURL} = repo;
        return (
          <S.RepoListItem
            key={id}
            to={getSetActiveRepoLink(repo)}
            onClick={() => {
              window.analytics?.track(`FC Project Clicked`, {
                location: `FC projects page`,
                projectID: id,
                projectName: name,
              });
            }}>
            <S.RepoListItemImage src={imageURL} />
            <S.RepoListItemContent>
              <S.RepoListItemHeaderRow>
                <S.RepoListItemHeader>{name}</S.RepoListItemHeader>
                <S.RepoListItemStats>
                  <S.RepoListItemStat>
                    <Icon name="star" />
                    {stars}
                  </S.RepoListItemStat>
                  <S.RepoListItemStat>
                    <Icon name="fork" />
                    {forks}
                  </S.RepoListItemStat>
                  {/* <S.RepoListItemStat>
                  <Icon name="find" />
                  {watching}
                </S.RepoListItemStat>
                <S.RepoListItemStat>
                  <Icon name="fire" />
                  {0}
                </S.RepoListItemStat> */}
                </S.RepoListItemStats>
              </S.RepoListItemHeaderRow>
              <S.RepoListItemBody>
                {description}
                <ProjectUsageChart repoID={id} />
              </S.RepoListItemBody>
            </S.RepoListItemContent>
          </S.RepoListItem>
        );
      })}
    </S.RepoList>
  );
});

type RepoGitHubMetadata = {
  stars: number;
  watching: number;
  forks: number;
};

type RepoWithGitHubMetadata = RepoWithUsage & RepoGitHubMetadata;

const RepoPage: React.FC = React.memo(() => {
  const {reposWithGitHubMetadata: repos, activeRepo} = useFCContext();

  const repo = useMemo(
    () => repos.find(r => r.linkForm === activeRepo),
    [repos, activeRepo]
  );

  const links = useMemo(
    () => repo?.links.filter(l => !l.pending) ?? [],
    [repo]
  );

  if (repo == null) {
    return null;
  }

  return (
    <S.RepoPage>
      <RepoSidebar repo={repo} />
      <S.RepoContent className="main-page">
        <ProjectUsageChart repoID={repo.id} />
        <S.RepoCustomContent>
          <Markdown content={repo.customContent} />
        </S.RepoCustomContent>
        {links.length > 0 && (
          <>
            <div className="header-wrapper">
              <ImageIconHeader icon="gallery" text="Links" />
            </div>
            <div className="report-showcase">
              <Card.Group itemsPerRow={2}>
                {links.map(({id, url, name, description, imageURL}) => {
                  return (
                    <Link
                      key={id}
                      to={url}
                      className={`ui card report-showcase-item`}>
                      <Card.Content className="report-showcase-item-content">
                        <img
                          src={imageURL}
                          className={`report-showcase-item-image`}
                          alt={`link thumbnail`}
                        />
                        <div className="report-showcase-item-text">
                          <Card.Header className="report-showcase-item-title">
                            {name}
                          </Card.Header>
                          <Card.Description className="report-showcase-item-description">
                            {description}
                          </Card.Description>
                        </div>
                      </Card.Content>
                    </Link>
                  );
                })}
              </Card.Group>
            </div>
          </>
        )}
      </S.RepoContent>
    </S.RepoPage>
  );
});

type RepoSidebarProps = {repo: RepoWithGitHubMetadata};

const RepoSidebar: React.FC<RepoSidebarProps> = React.memo(
  ({repo: {name, description, imageURL, stars, watching, forks}}) => {
    return (
      <div className="sidebar profile-sidebar">
        <div className="inner-content">
          <S.RepoImage src={imageURL} />
          <S.RepoSidebarName>{name}</S.RepoSidebarName>
          <S.RepoDescription>{description}</S.RepoDescription>
          <S.RepoGitHubMetadata>
            <S.RepoGitHubMetadataRow>
              <S.RepoGitHubMetadataIcon name="star" /> {stars}
            </S.RepoGitHubMetadataRow>
            <S.RepoGitHubMetadataRow>
              <S.RepoGitHubMetadataIcon name="fork" />
              {forks}
            </S.RepoGitHubMetadataRow>
          </S.RepoGitHubMetadata>
        </div>
      </div>
    );
  }
);

type ProjectUsageChartProps = {repoID: string};

const ProjectUsageChart: React.FC<ProjectUsageChartProps> = React.memo(
  ({repoID}) => {
    const {reposWithGitHubMetadata: repos} = useFCContext();

    const repo = repos.find(r => r.id === repoID);
    if (repo == null) {
      return null;
    }

    const userCountBars = repo.userCounts
      .filter(c => c.userCount > 4)
      .map(c => ({key: c.weekStr, value: c.userCount}));
    if (userCountBars.length < 10) {
      return null;
    }

    return (
      <S.RepoListChartContainer>
        <BarChart
          vertical
          enableTooltip={false}
          enableAxisTicks={false}
          bars={_.initial(userCountBars)}
        />
      </S.RepoListChartContainer>
    );
  }
);

async function getGitHubMetadataForRepo({
  url,
}: RepoWithUsage): Promise<RepoGitHubMetadata> {
  const [owner, repoName] = url.split('/').slice(-2);
  // eslint-disable-next-line wandb/no-unprefixed-urls
  const resp = await fetch(`https://api.github.com/repos/${owner}/${repoName}`);
  const json = await resp.json();
  return {
    stars: json.stargazers_count,
    watching: json.watchers_count,
    forks: json.forks_count,
  };
}

type TopicPageProps = {};

const TopicPage: React.FC<TopicPageProps> = React.memo(() => {
  const {tagsV2, activeTopic, reportIDSetByTagName, getSetActiveTagLink} =
    useFCContext();
  if (activeTopic == null) {
    return null;
  }

  const topic = tagsV2.find(t => t.linkForm === activeTopic);
  if (topic == null) {
    return null;
  }

  const topicChildren = _.sortBy(
    tagsV2.filter(t => t.parent === topic.id),
    t => -(reportIDSetByTagName.get(t.name)?.size ?? 0)
  );

  return (
    <>
      <S.TopicPageHeader>{topic.name}</S.TopicPageHeader>
      <S.TopicPageTags>
        {topicChildren.map(({id, name, linkForm, imageURL}) => (
          <S.TopicPageTag key={id} to={getSetActiveTagLink(linkForm)}>
            <S.TopicPageTagImage src={imageURL || `/logo.png`} />
            <S.TopicPageTagName>{name}</S.TopicPageTagName>
          </S.TopicPageTag>
        ))}
      </S.TopicPageTags>
    </>
  );
});

type SubscribeModalProps = {};

const SubscribeModal: React.FC<SubscribeModalProps> = React.memo(() => {
  const {subscribeModalOpen} = useFCContext();
  const {setSubscribeModalOpen} = useFCContextUpdaters();

  const viewer = useViewer();
  const loggedIn: boolean = useMemo(() => viewer != null, [viewer]);

  const [createNewsletterSubscription] =
    useCreateNewsletterSubscriptionMutation({
      context: propagateErrorsContext(),
    });

  const subscriptionInputRef = useRef<HTMLDivElement>(null);

  const [subscriptionInput, setSubscriptionInput] = useState('');
  const [subscribeSuccess, setSubscribeSuccess] = useState(false);
  const [subscribeError, setSubscribeError] = useState('');

  const createSubscription = useCallback(async () => {
    try {
      if (!loggedIn && !isValidEmail(subscriptionInput)) {
        setSubscribeError('Please enter a valid email.');
        subscriptionInputRef.current?.focus();
        return;
      }

      const mutationVars = loggedIn ? {} : {email: subscriptionInput};
      await createNewsletterSubscription({variables: mutationVars});

      setSubscribeSuccess(true);
      setSubscriptionInput('');
      setSubscribeError('');
    } catch (err) {
      console.error(err);
      subscriptionInputRef.current?.focus();
      setSubscriptionInput('');
      setSubscribeError('Oops, something went wrong.');
    }
  }, [loggedIn, subscriptionInput, createNewsletterSubscription]);

  if (!subscribeModalOpen) {
    return null;
  }
  return (
    <S.SubscribeModal open onClose={() => setSubscribeModalOpen(false)}>
      <S.SubscribeSection>
        <S.SubscribeHeader>
          Get weekly updates with the latest ML news.
        </S.SubscribeHeader>
        {subscribeSuccess ? (
          <S.SubscribeSuccessMessage>
            <WBIcon name="check" />
            Subscribed!
          </S.SubscribeSuccessMessage>
        ) : (
          <S.SubscribeInputContainer>
            <S.SubscribeInputMessageContainer loggedIn={loggedIn}>
              <S.SubscribeInput
                placeholder="Email Address"
                invalid={subscribeError !== ''}
                value={subscriptionInput}
                ref={subscriptionInputRef}
                onChange={
                  ((e, {value}) =>
                    setSubscriptionInput(value)) as StrictInputProps['onChange']
                }
              />
              <S.SubscribeInvalidMessage invalid={subscribeError !== ''}>
                {subscribeError}
              </S.SubscribeInvalidMessage>
            </S.SubscribeInputMessageContainer>
            <S.SubscribeButton
              content="Subscribe"
              onClick={createSubscription}
            />
          </S.SubscribeInputContainer>
        )}
      </S.SubscribeSection>
    </S.SubscribeModal>
  );
});

type SubmitPostModalProps = {};

const SubmitPostModal: React.FC<SubmitPostModalProps> = React.memo(() => {
  const viewer = useViewer();
  const {submitPostModalOpen} = useFCContext();
  const {setSubmitPostModalOpen} = useFCContextUpdaters();

  const {width: viewportWidth} = useWindowSize();
  const isMobile = useMemo(
    () => viewportWidth <= tabletBreakpoint,
    [viewportWidth]
  );

  if (!submitPostModalOpen) {
    return null;
  }

  const showSignupStep = viewer == null;

  return (
    <S.SubmitPostModal open onClose={() => setSubmitPostModalOpen(false)}>
      <HomePageWithDataS.StartGuide
        embedded
        isMobile={isMobile}
        data-test="home-get-started">
        <h1>Submit a post on Fully Connected</h1>
        <h2>
          Got a new project, tool, paper, tutorial, event or another ML resource
          to share with the community?
        </h2>
        {showSignupStep && (
          <HomePageWithDataS.StartGuideSectionContainer border>
            <HomePageWithDataS.StartGuideSectionHeader>
              <div>1</div>
              <div>Signup or sign in to your W&B Account</div>
            </HomePageWithDataS.StartGuideSectionHeader>
            <HomePageWithDataS.StartGuideSection>
              <TargetBlank href={URLs.createFCPost()}>
                <Button primary data-test="signup" content="Sign Up" />
              </TargetBlank>
            </HomePageWithDataS.StartGuideSection>
          </HomePageWithDataS.StartGuideSectionContainer>
        )}
        <HomePageWithDataS.StartGuideSectionContainer border>
          <HomePageWithDataS.StartGuideSectionHeader>
            <div>{showSignupStep ? 2 : 1}</div>
            <div>Write your post</div>
          </HomePageWithDataS.StartGuideSectionHeader>
          <HomePageWithDataS.StartGuideSection>
            <img src={`/fc/publish-post.gif`} alt="publish post" />
          </HomePageWithDataS.StartGuideSection>
        </HomePageWithDataS.StartGuideSectionContainer>
        <HomePageWithDataS.StartGuideSectionContainer>
          <HomePageWithDataS.StartGuideSectionHeader>
            <div>{showSignupStep ? 3 : 2}</div>
            <div>Submit to Fully Connected</div>
          </HomePageWithDataS.StartGuideSectionHeader>
          <HomePageWithDataS.StartGuideSectionSubheader>
            We review all submissions within 24 hours!
          </HomePageWithDataS.StartGuideSectionSubheader>
        </HomePageWithDataS.StartGuideSectionContainer>
      </HomePageWithDataS.StartGuide>
      <S.SubmitPostModalActions>
        <Button
          content="Cancel"
          onClick={() => setSubmitPostModalOpen(false)}
        />
        <TargetBlank href={URLs.createFCPost()}>
          <Button primary content="Begin" />
        </TargetBlank>
      </S.SubmitPostModalActions>
    </S.SubmitPostModal>
  );
});

type SubmitLinkModalProps = {};

const SubmitLinkModal: React.FC<SubmitLinkModalProps> = React.memo(() => {
  const {
    activeRepo,
    reposWithGitHubMetadata: repos,
    submitLinkModalOpen,
  } = useFCContext();
  const {setSubmitLinkModalOpen} = useFCContextUpdaters();

  const [submitFCProject] = useSubmitFcProjectMutation();
  const {refetch: refetchGallery} = useGalleryQuery();

  const [url, setURL] = useState(``);
  const [name, setName] = useState(``);
  const [description, setDescription] = useState(``);
  const isValid = !!url && !!name && !!description;

  const [submitting, setSubmitting] = useState(false);

  const repo = useMemo(
    () => repos.find(r => r.linkForm === activeRepo),
    [repos, activeRepo]
  );

  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (submitLinkModalOpen) {
      inputRef.current?.focus();
    }
  }, [submitLinkModalOpen]);

  const onSubmit = useCallback(async () => {
    const properties = {
      url,
      name,
      description,
      addedAt: new Date().toJSON(),
      pending: true,
    };
    const initializeBase = repo == null ? newRepo : newRepoLink;
    await submit({...initializeBase(), ...properties}, repo?.id);

    async function submit(spec: Repo | RepoLink, fcProjectID?: string) {
      setSubmitting(true);
      try {
        await submitFCProject({
          variables: {
            spec: JSON.stringify(spec),
            fcProjectID,
            sendEmail: true,
          },
        });
        await refetchGallery();
        alert(
          `Thank you for submitting! We will review your submission within 24 hours.`
        );
        setSubmitLinkModalOpen(false);
      } catch (err) {
        console.error(`Failed to submit FC project`, err);
        alert(
          `We're sorry, something went wrong. Please contact support@wandb.com to report this issue.`
        );
      } finally {
        setSubmitting(false);
      }
    }
  }, [
    url,
    name,
    description,
    repo,
    submitFCProject,
    refetchGallery,
    setSubmitLinkModalOpen,
  ]);

  if (!submitLinkModalOpen) {
    return null;
  }

  return (
    <S.Modal open onClose={() => setSubmitLinkModalOpen(false)}>
      <Modal.Content>
        <S.ModalHeader>
          Submit a {repo == null ? `project` : `link`}
        </S.ModalHeader>
        <S.ModalSubheader>
          We review all submissions within 24 hours
        </S.ModalSubheader>
        <Form>
          <S.ModalField>
            <S.ModalLabel>
              URL <S.ModalLabelRequired>*</S.ModalLabelRequired>
            </S.ModalLabel>
            <S.ModalInput
              ref={inputRef}
              value={url}
              onChange={
                ((e, {value: v}) => setURL(v)) as StrictInputProps['onChange']
              }
            />
          </S.ModalField>
          <S.ModalField>
            <S.ModalLabel>
              Name <S.ModalLabelRequired>*</S.ModalLabelRequired>
            </S.ModalLabel>
            <S.ModalInput
              value={name}
              onChange={
                ((e, {value: v}) => setName(v)) as StrictInputProps['onChange']
              }
            />
          </S.ModalField>
          <S.ModalField>
            <S.ModalLabel>
              Description <S.ModalLabelRequired>*</S.ModalLabelRequired>
            </S.ModalLabel>
            <S.ModalTextArea
              value={description}
              onChange={
                ((e, {value: v}) =>
                  setDescription(v)) as StrictInputProps['onChange']
              }
            />
          </S.ModalField>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => setSubmitLinkModalOpen(false)}>Cancel</Button>
        <Button
          primary
          disabled={submitting || !isValid}
          loading={submitting}
          onClick={onSubmit}>
          Confirm
        </Button>
      </Modal.Actions>
    </S.Modal>
  );
});

type ResponsiveProps = {desktop: ReactNode; mobile: ReactNode};

const Responsive: React.FC<ResponsiveProps> = React.memo(
  ({desktop, mobile}) => {
    const {width: screenWidth} = useWindowSize();
    const isDesktop = screenWidth > S.TABLET_WIDTH;
    return <>{isDesktop ? desktop : mobile}</>;
  }
);

type ResponsiveSpaceProps = {};

const ResponsiveSpace: React.FC<ResponsiveSpaceProps> = React.memo(() => {
  return <Responsive desktop={` `} mobile={<br />} />;
});

type DesktopProps = {};

const Desktop: React.FC<DesktopProps> = React.memo(({children}) => {
  return <Responsive desktop={children} mobile={null} />;
});

type MobileProps = {};

const Mobile: React.FC<MobileProps> = React.memo(({children}) => {
  return <Responsive desktop={null} mobile={children} />;
});

type FCReportsResult = {
  loadingReports: boolean;
  newReports: ReportMetadata[];
  popularReports: ReportMetadata[];
  reports: ReportMetadata[];
  updateReportMetadata: (
    id: string,
    updateFn: (r: ReportMetadata) => void
  ) => void;
};

function useFCReports({
  reportIDsWithTagV2IDs,
  tagsV2,
  activeTag,
  language,
  searchQueryDebounced,
  reportBelongsToTag,
}: Pick<
  FCContextState,
  | 'reportIDsWithTagV2IDs'
  | 'tagsV2'
  | 'activeTag'
  | 'language'
  | 'searchQueryDebounced'
  | 'reportBelongsToTag'
>): FCReportsResult {
  const {
    loading: reportMetadataLoading,
    reportMetadatas,
    updateReportMetadata,
  } = useReportMetadataWithCache({
    reportIDsWithTagV2IDs,
    tags: tagsV2,
    activeTag,
    fetchAll: searchQueryDebounced !== '',
    reportBelongsToTag,
  });

  const reports = useMemo(
    () => filterAndSortReports(reportMetadatas, language),
    [reportMetadatas, language]
  );

  const {newReports, popularReports, notFeaturedReports} = useMemo(
    () =>
      splitReportsByFeatured({
        reports,
        tags: tagsV2,
        activeTag,
        language,
        reportBelongsToTag,
      }),
    [reports, tagsV2, activeTag, language, reportBelongsToTag]
  );

  const searchedReports = useMemo(() => {
    if (searchQueryDebounced === '') {
      return notFeaturedReports;
    }
    return fuzzyMatchWithMapping(
      reports,
      searchQueryDebounced,
      (r: ReportMetadata) => r.displayName
    );
  }, [notFeaturedReports, reports, searchQueryDebounced]);

  return {
    loadingReports: reportMetadataLoading,
    newReports,
    popularReports,
    reports: searchedReports,
    updateReportMetadata,
  };
}

function filterAndSortReports(
  reports: ReportMetadata[],
  language: Language
): ReportMetadata[] {
  return _.sortBy(
    reports
      .filter(r => !r.pending)
      .filter(reportIsPastPublishDate)
      .filter(r => r.language === language),
    r => {
      const addedAtTS = new Date(r.addedAt ?? '').valueOf();
      return -addedAtTS;
    }
  );
}

function reportIsPastPublishDate(r: ReportMetadata): boolean {
  if (r.addedAt == null) {
    return false;
  }
  const addedAt = new Date(r.addedAt);
  const now = new Date();
  return now >= addedAt;
}

type ReportsSplitByFeatured = {
  newReports: ReportMetadata[];
  popularReports: ReportMetadata[];
  notFeaturedReports: ReportMetadata[];
};

type SplitReportsByFeaturedParams = {
  reports: ReportMetadata[];
  tags: TagV2[];
  activeTag: string;
  language: Language;
  reportBelongsToTag: (reportID: string, tagName: string) => boolean;
};

function splitReportsByFeatured({
  reports,
  tags,
  activeTag,
  language,
  reportBelongsToTag,
}: SplitReportsByFeaturedParams): ReportsSplitByFeatured {
  const tag = tags.find(t => t.name === activeTag);
  if (tag == null) {
    return {newReports: [], popularReports: [], notFeaturedReports: []};
  }

  // const announcementReportIDSet = new Set(
  //   reports.filter(r => r.announcement).map(r => r.id)
  // );
  // const categoryNameSet = new Set(categories.map(t => t.name));
  // const tagIsCategory = (tagName: string) => categoryNameSet.has(tagName);
  // const announcementAndFeaturedIDs = _.uniq([
  //   // only show announcements when active tag is a category
  //   ...(tagIsCategory(activeTag) ? announcementReportIDSet : []),
  //   ...tag.featuredReportIDs,
  // ]);
  // const announcementAndFeaturedIDSet = new Set(announcementAndFeaturedIDs);

  const reportsByID = new Map<string, ReportMetadata>(
    reports.map(r => [r.id, r])
  );
  const featuredReportIDSet = new Set(tag.featuredReports.map(r => r.reportID));
  const tagReports = reports.filter(r => reportBelongsToTag(r.id, activeTag));

  return {
    newReports: tag.featuredReports
      .filter(r => r.category === `new`)
      .map(r => reportsByID.get(r.reportID))
      .filter(isNotNullOrUndefined)
      .filter(r => r.language === language),
    popularReports: tag.featuredReports
      .filter(r => r.category === `popular`)
      .map(r => reportsByID.get(r.reportID))
      .filter(isNotNullOrUndefined)
      .filter(r => r.language === language),
    notFeaturedReports: tagReports.filter(
      ({id}) => !featuredReportIDSet.has(id)
    ),
  };
}

type PaginationResult<T> = {
  currentItems: T[];
  minPage: number;
  maxPage: number;
};

type PaginationParams<T> = {
  loadingItems: boolean;
  allItems: T[];
  itemsPerPage: number;
  currentPage: number;
  setCurrentPage: (n: number) => void;
};

function usePagination<T>({
  loadingItems,
  allItems,
  itemsPerPage,
  currentPage,
  setCurrentPage,
}: PaginationParams<T>): PaginationResult<T> {
  const total = allItems.length;
  const minPage = 1;
  const maxPage = Math.max(Math.ceil(total / itemsPerPage), 1);

  const currentItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return allItems.slice(startIndex, startIndex + itemsPerPage);
  }, [allItems, currentPage, itemsPerPage]);

  // Clamp to last page if out of bounds
  useLayoutEffect(() => {
    if (!loadingItems && currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingItems, currentPage, maxPage]);

  return {
    currentItems,
    minPage,
    maxPage,
  };
}

type PaginationProps = {};

const Pagination: React.FC<PaginationProps> = React.memo(() => {
  const location = useLocation();
  const {currentPage, minPage, maxPage, getSetCurrentPageLink} = useFCContext();

  const pagesWithEllipses: Array<number | 'ellipsis'> = useMemo(() => {
    if (maxPage - minPage < 5) {
      return _.range(minPage, maxPage + 1);
    }
    if (currentPage <= minPage + 2) {
      return [minPage, minPage + 1, minPage + 2, 'ellipsis', maxPage];
    }
    if (currentPage >= maxPage - 2) {
      return [minPage, 'ellipsis', maxPage - 2, maxPage - 1, maxPage];
    }
    return [minPage, 'ellipsis', currentPage, 'ellipsis', maxPage];
  }, [currentPage, minPage, maxPage]);

  const nextPageLink =
    currentPage < maxPage ? getSetCurrentPageLink(currentPage + 1) : undefined;
  const prevPageLink =
    currentPage > minPage ? getSetCurrentPageLink(currentPage - 1) : undefined;

  const renderPage = (p: number | 'ellipsis', i: number) => {
    if (p === 'ellipsis') {
      return (
        <S.PaginationPageDisabled key={i} disabled>
          <S.PaginationEllipsis>...</S.PaginationEllipsis>
        </S.PaginationPageDisabled>
      );
    }
    return (
      <S.PaginationPage
        key={i}
        to={getSetCurrentPageLink(p)}
        active={currentPage === p}>
        {p}
      </S.PaginationPage>
    );
  };

  return (
    <S.PaginationContainer>
      <S.Pagination>
        <S.PaginationEdge
          to={prevPageLink ?? location}
          disabled={prevPageLink == null}>
          <S.PaginationIcon name={`chevron-left`} />
          <Desktop>Previous</Desktop>
        </S.PaginationEdge>
        <S.PaginationPages>
          {pagesWithEllipses.map(renderPage)}
        </S.PaginationPages>
        <S.PaginationEdge
          to={nextPageLink ?? location}
          disabled={nextPageLink == null}>
          <Desktop>Next</Desktop>
          <S.PaginationIcon name={`chevron-right`} />
        </S.PaginationEdge>
      </S.Pagination>
    </S.PaginationContainer>
  );
});

type LoadingContentProps = {};

const LoadingContent: React.FC<LoadingContentProps> = React.memo(() => {
  return (
    <S.LoaderContainer>
      <S.Loader name="loading-content" />
    </S.LoaderContainer>
  );
});

type StateFromURL = {
  page: FCPage;
  activeTag: string;
  activeReportID: string | null;
  activeRepo: string | null;
  activeTopic: string | null;
  language: Language;
  currentPage: number;
};

type DeriveStateFromURLParams = {
  params: RouteParams;
  reportIDsWithTagV2IDs: ReportIDWithTagIDs[];
  tags: TagV2[];
  homeCategoryName: string;
};

function deriveStateFromURL({
  params,
  reportIDsWithTagV2IDs,
  tags,
  homeCategoryName,
}: DeriveStateFromURLParams): StateFromURL {
  if (isReportPageParams(params)) {
    const activeReportID =
      parseNameAndIDSafe(params.reportNameAndID)?.id ?? null;
    const tagPage = deriveTagPage({
      reportIDsWithTagV2IDs,
      tags,
      activeReportID,
    });
    return {
      page: tagPage ?? `blog`,
      activeTag: ``,
      activeReportID,
      activeRepo: null,
      activeTopic: null,
      language: deriveLanguage(),
      currentPage: deriveCurrentPage(),
    };
  }

  if (isRepoParams(params)) {
    return {
      page: `projects`,
      activeTag: ``,
      activeReportID: null,
      activeRepo: params.repoName,
      activeTopic: null,
      language: deriveLanguage(),
      currentPage: deriveCurrentPage(),
    };
  }

  if (isTopicParams(params)) {
    return {
      page: `topics`,
      activeTag: ``,
      activeReportID: null,
      activeRepo: null,
      activeTopic: params.topic,
      language: deriveLanguage(),
      currentPage: deriveCurrentPage(),
    };
  }

  const {page, tag} = params;
  return {
    page: derivePage({pageParam: page}),
    activeTag: deriveActiveTag({
      pageParam: page,
      tagParam: tag,
      tags,
      homeCategoryName,
    }),
    activeReportID: null,
    activeRepo: null,
    activeTopic: null,
    language: deriveLanguage(),
    currentPage: deriveCurrentPage(),
  };
}

type DerivePageParams = {pageParam: string | undefined};

type DeriveTagPageParams = {
  reportIDsWithTagV2IDs: ReportIDWithTagIDs[];
  tags: TagV2[];
  activeReportID: string | null;
};

function deriveTagPage({
  reportIDsWithTagV2IDs,
  tags,
  activeReportID,
}: DeriveTagPageParams): FCPage | null {
  if (activeReportID == null) {
    return null;
  }

  const reportIDWithTagV2IDs = reportIDsWithTagV2IDs.find(
    r => r.id === activeReportID
  );
  if (reportIDWithTagV2IDs == null) {
    return null;
  }

  const tagPageLinkFormSet: Set<string> = new Set(TAG_PAGES);
  const tagPageByID = new Map(
    tags.filter(t => tagPageLinkFormSet.has(t.linkForm)).map(t => [t.id, t])
  );

  return (
    reportIDWithTagV2IDs.tagIDs
      .map(tagID => tagPageByID.get(tagID))
      .filter(isNotNullOrUndefined)
      .map(t => t.linkForm)
      .filter(isFCPage)[0] ?? null
  );
}

function derivePage({pageParam}: DerivePageParams): FCPage {
  if (pageParam != null && isFCPage(pageParam)) {
    return pageParam;
  }
  return MAIN_PAGE;
}

type DeriveActiveTagParams = {
  pageParam: string | undefined;
  tagParam: string | undefined;
  tags: TagV2[];
  homeCategoryName: string;
};

function deriveActiveTag({
  pageParam,
  tagParam,
  tags,
  homeCategoryName,
}: DeriveActiveTagParams): string {
  const effectiveTagParam = isOneOf(pageParam, TAG_PAGES)
    ? pageParam
    : tagParam;
  if (effectiveTagParam == null) {
    return homeCategoryName;
  }
  return formatGalleryTag(tags, effectiveTagParam);
}

function deriveLanguage(): Language {
  const {language} = qs.parse(history.location.search);
  if (
    language != null &&
    typeof language === 'string' &&
    isLanguage(language)
  ) {
    return language;
  }
  return DEFAULT_LANGUAGE;
}

function deriveCurrentPage(): number {
  const {page} = qs.parse(history.location.search);
  if (page != null && typeof page === 'string') {
    return Number(page);
  }
  return 1;
}

function useActiveReportParams(reportID: string | null): {
  loading: boolean;
  reportParams: ReportParams | null;
} {
  const {loading, reportMetadatas} = useReportMetadata(
    reportID != null ? [reportID] : []
  );

  const reportParams = useMemo(() => {
    if (reportMetadatas == null || reportMetadatas.length === 0) {
      return null;
    }
    const {id, entityName, project, displayName} = reportMetadatas[0];
    return {
      id,
      entityName: project?.entityName || entityName,
      projectName: project?.name,
      displayName,
    };
  }, [reportMetadatas]);

  return {loading, reportParams};
}

function useGitHubMetadata(repos: RepoWithUsage[]): RepoWithGitHubMetadata[] {
  const [reposWithGitHubMetadata, setReposWithGitHubMetadata] = useState<
    RepoWithGitHubMetadata[]
  >([]);

  useEffect(() => {
    (async () => {
      const metadatas = await Promise.all(repos.map(getGitHubMetadataForRepo));
      setReposWithGitHubMetadata(
        repos.map((r, i) => ({...r, ...metadatas[i]}))
      );
    })();
  }, [repos]);

  return reposWithGitHubMetadata;
}
